import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import { FlexCol, FlexRowCenterVertical } from '../../Defaults/Flexbox/Flexbox';
import { Title } from '../General/Title';
import InfoRow from './InfoRow';
import { PriceInfo } from './PriceInfo';
import { CountSetter } from './CountSetter';
import {
  ButtonPrimary,
  ButtonPrimaryLink,
  ButtonSecondary,
} from '../General/Button';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { Select } from '../General/Select';
import { prop } from '../../../utilities';
import StockInfoRow from './StockInfoRow';
import { getNumberFromString } from '../../../utilities/selectors';
import { AddToWishlsit } from './AddToWishlsit';

interface Good {
  good_id: number;
  attribs: Array<{
    attrib_id: number;
    attrib_value: string;
    value_id: number;
    attrib_name: string;
  }>;
  oldprice: number;
  availability: {
    order_available: number;
  };
  web_attribs: any;
  on_stock_count: number;
}

interface SelectProps {
  name: string;
  value: string;
}

interface Props {
  title: string;
  shortDescription: string;
  plu: string;
  warranty: string;
  manufacturer: string;
  pckg: string;
  onStock: number;
  price: number | string;
  priceWithoutVat: number | string;
  currency: string;
  unit: string;
  count: number;
  addToCart: (selectedGoodId: number) => void;
  shareButton: any;
  handleCountChange: (newValue) => void;
  handleGoodChange: any;
  variants: Good[];
  goods: Good[];
  ean: string;
  oldPrice: number;
  saleByPackage: number;
  queryVariantId: number | null;
  updatedGoodByQuery: boolean;
  setUpdatedGoodByQuery: (setUpdatedGoodByQuery: boolean) => void;
  addToWishList: () => void;
  internalCode: string;
}

function RightUpper({
  title,
  shortDescription,
  plu,
  ean,
  warranty,
  manufacturer,
  pckg,
  onStock,
  price,
  priceWithoutVat,
  currency,
  unit,
  count,
  addToCart,
  shareButton,
  handleCountChange,
  handleGoodChange,
  variants,
  goods,
  saleByPackage,
  queryVariantId,
  updatedGoodByQuery,
  setUpdatedGoodByQuery,
  addToWishList,
  internalCode,
}: Props) {
  const [selectedGoodId, setSelectedGoodId] = React.useState(-1);
  const [activeAttribs, setActiveAttribs] = React.useState({});

  const selectedGood = goods.find(g => g.good_id === selectedGoodId);
  const inPackage = getNumberFromString(pckg);
  const step = inPackage > 0 ? inPackage : 1;
  const correctStep = saleByPackage ? step : 1;
  const priceCounted = Number(price) * (count > 0 ? count : 1);
  const priceWithoutVatCounted =
    Number(priceWithoutVat) * (count > 0 ? count : 1);
  const oldPriceCounted = Number(prop(selectedGood, 'oldprice', 0)) * count;

  const attribsToHide: number[] = [];
  const attribsObj = {};
  let goodAttribsObj = {};
  if (goods.length > 0) {
    goods.map(good => {
      const onStockCount = good.on_stock_count;
      if (onStockCount === 0) {
        return;
      }

      const { web_attribs } = good;
      if (web_attribs && web_attribs.length) {
        web_attribs.map(webAttrib => {
          if (!attribsObj[webAttrib.attrib_id]) {
            attribsObj[webAttrib.attrib_id] = [];
          }

          if (
            !attribsObj[webAttrib.attrib_id].find(
              a => prop(a, 'valueId') === prop(webAttrib, 'values.0.value_id'),
            )
          ) {
            attribsObj[webAttrib.attrib_id].push({
              values: prop(webAttrib, 'values.0'),
              id: webAttrib.attrib_id,
              name: prop(webAttrib, 'attrib_name'),
              valueId: prop(webAttrib, 'values.0.value_id'),
            });
          }

          if (!attribsToHide.includes(webAttrib.attrib_id)) {
            attribsToHide.push(webAttrib.attrib_id);
          }
        });
      }
    });

    goodAttribsObj = goods.reduce((prev, current) => {
      let attribString = '';
      if (current.web_attribs) {
        current.web_attribs.map(w => {
          if (attribString) {
            attribString += '|';
          }
          attribString += `${prop(w, 'values.0.attrib_id')}-${prop(
            w,
            'values.0.value_id',
          )}`;
        });

        return {
          ...prev,
          [attribString]: current,
        };
      }

      return prev;
    }, {});
  }

  let activeAttribString = '';
  Object.keys(activeAttribs).map(key => {
    const a = activeAttribs[key];
    if (activeAttribString) {
      activeAttribString += '|';
    }
    activeAttribString += `${key}-${a}`;
  });
  React.useEffect(() => {
    if (goods && goods.length > 0) {
      const selectedG: any =
        goods.filter((g: any) => g.on_stock_count > 0)[0] || goods[0];
      setSelectedGoodId(selectedG.good_id);
    }
  });

  React.useEffect(() => {
    if (
      selectedGoodId &&
      goodAttribsObj &&
      activeAttribString === '' &&
      queryVariantId &&
      !updatedGoodByQuery
    ) {
      const attribsObjAfterLoad = Object.keys(goodAttribsObj).reduce(
        (acc, key) => {
          const good = goodAttribsObj[key];
          if (good.good_id === selectedGoodId) {
            const attribsAfterLoad = key.split('|');
            attribsAfterLoad.forEach(attrib => {
              const [attribId, valueId] = attrib.split('-');
              if (attribId && valueId) {
                acc[attribId] = valueId;
              }
            });
          }
          return acc;
        },
        {},
      );

      if (Object.keys(attribsObjAfterLoad).length > 0) {
        setActiveAttribs(attribsObjAfterLoad);
        setUpdatedGoodByQuery(true);
      }
    }
  }, [selectedGoodId, goodAttribsObj, activeAttribs, activeAttribString]);

  const handleAttribSelectChange = (e: any, attribId: any) => {
    const valueId = e.target.value;
    if (valueId) {
      const activeAttribsKeys = Object.keys(activeAttribs);
      const attribPosition = activeAttribsKeys.indexOf(attribId.toString());
      const higherKeys =
        attribPosition === -1
          ? []
          : activeAttribsKeys.slice(attribPosition + 1);
      if (activeAttribsKeys.length > 0 && attribPosition === 0) {
        setActiveAttribs({ [attribId]: valueId });
      } else {
        const newAttribs = { ...activeAttribs, [attribId]: valueId };
        higherKeys.forEach(h => {
          delete newAttribs[h];
        });
        setActiveAttribs(newAttribs);
      }
    }
  };

  const activeGood = prop(goodAttribsObj, `${activeAttribString}`);
  if (activeGood) {
    const goodId = activeGood.good_id;
    handleGoodChange(Number(goodId));
  }

  const goodAttribsKeysArray = Object.keys(goodAttribsObj);
  const isMissingAttribs =
    Object.keys(attribsObj).length === 0 ? false : !activeGood;

  const handleClick = () => {
    if (activeGood) {
      addToCart(activeGood.good_id);
    } else if (selectedGoodId) {
      addToCart(selectedGoodId);
    }
  };
  const resetAttribs = () => {
    setActiveAttribs({});
  };

  const isInWishlist =
    prop(activeGood, 'is_favorite') || prop(selectedGood, 'is_favorite', false);

  const renderAttribs = () => {
    return (
      <AttribWrapper>
        {attribsObj &&
          Object.keys(attribsObj).map((key, i) => {
            const attrib = attribsObj[key];
            const isDisabled =
              Object.keys(activeAttribs).length >= i ? false : true;
            const val = prop(activeAttribs, `${prop(attrib, '0.id')}`);
            const attribId = prop(attrib, '0.id');
            return (
              <AttribSelectWrapper key={prop(attrib, '0.id')}>
                <Select
                  label={prop(attrib, '0.name')}
                  value={val || []}
                  options={attrib
                    .map(a => {
                      if (
                        i > 0 &&
                        !goodAttribsKeysArray.find(goodAttribsKey => {
                          return (
                            goodAttribsKey.includes(
                              activeAttribString.replace(
                                `|${prop(attrib, '0.id')}-${prop(
                                  activeAttribs,
                                  `${key}.0.value`,
                                )}`,
                                '',
                              ),
                            ) &&
                            goodAttribsKey.includes(
                              `${prop(attrib, '0.id')}-${prop(
                                a,
                                'values.value_id',
                              )}`,
                            )
                          );
                        })
                      ) {
                        return null;
                      }
                      return {
                        label: prop(a, 'values.attrib_value'),
                        value: prop(a, 'values.value_id'),
                        name: prop(a, 'values.attrib_value'),
                      };
                    })
                    .filter(a => a)
                    .sort((a, b) => a.name.localeCompare(b.name))}
                  // tslint:disable-next-line:jsx-no-lambda
                  onChange={e => handleAttribSelectChange(e, attribId)}
                  name={`attrib_name_${prop(attrib, '0.name')}`}
                  placeholder={prop(attrib, '0.name')}
                  disabled={isDisabled}
                  required={true}
                  maxWidth={200}
                  mobileMarginBottom={4}
                />
              </AttribSelectWrapper>
            );
          })}
      </AttribWrapper>
    );
  };

  const bottomPartDesktop = () => {
    return (
      <>
        <PriceInfo
          oldPrice={oldPriceCounted}
          price={priceCounted}
          priceWithoutVat={priceWithoutVatCounted}
          currency={currency}
        />
        {renderAttribs()}
        {!isMissingAttribs && Object.keys(attribsObj).length !== 0 ? (
          <AttribRessetWrapper>
            <AttribRessetButton onClick={resetAttribs}>
              {__('Resetovať parametre')}{' '}
              <SvgIcon
                width={18}
                height={18}
                icon={IconType.resetFilter}
                fill={'#FFFFFF'}
                alt={__('Košík')}
                title={__('Košík')}
                marginLeft={8}
              />
            </AttribRessetButton>
          </AttribRessetWrapper>
        ) : null}
        <PartWrapper>
          <CountSetter
            step={correctStep}
            unit={unit}
            count={count}
            onChange={handleCountChange}
            onStock={onStock}
          />
          <CartButton onClick={handleClick} disabled={isMissingAttribs}>
            <SvgIcon
              width={24}
              height={24}
              icon={IconType.newCart}
              fill={'#FFFFFF'}
              alt={__('Košík')}
              title={__('Košík')}
              marginRight={12}
            />
            {__('DO KOŠÍKA')}
          </CartButton>
          <AddToWishlsit
            addedToWishlist={isInWishlist}
            onClick={addToWishList}
          />
        </PartWrapper>
      </>
    );
  };

  const bottomPartMobile = () => {
    return (
      <>
        <PartWrapper>
          <PriceInfo
            oldPrice={prop(selectedGood, 'oldprice')}
            price={price}
            priceWithoutVat={priceWithoutVat}
            currency={currency}
          />
          {renderAttribs()}
          <CountWrapper>
            {!isMissingAttribs && Object.keys(attribsObj).length !== 0 ? (
              <AttribRessetWrapper>
                <AttribRessetButton onClick={resetAttribs}>
                  {__('Resetovať parametre')}{' '}
                  <SvgIcon
                    width={18}
                    height={18}
                    icon={IconType.resetFilter}
                    fill={'#FFFFFF'}
                    alt={__('Košík')}
                    title={__('Košík')}
                    marginLeft={8}
                  />
                </AttribRessetButton>
              </AttribRessetWrapper>
            ) : null}
            <CountSetter
              step={correctStep}
              unit={unit}
              count={count}
              onChange={handleCountChange}
              onStock={onStock}
            />
          </CountWrapper>
        </PartWrapper>
        <CartButton onClick={handleClick} disabled={isMissingAttribs}>
          <SvgIcon
            width={24}
            height={24}
            icon={IconType.newCart}
            fill={'#FFFFFF'}
            alt={__('Košík')}
            title={__('Košík')}
            marginRight={12}
          />
          {__('DO KOŠÍKA')}
        </CartButton>
        <AddToWishlsit
          addedToWishlist={isInWishlist}
          mobile={true}
          onClick={addToWishList}
        />
      </>
    );
  };

  const Variants = styled.div`
    margin-top: ${rem(24)};
  `;

  const rightUpperContent = () => {
    return (
      <>
        <ProductTitle marginTop={0} marginBottom={24}>
          {title}{' '}
        </ProductTitle>
        {shortDescription && typeof shortDescription === 'string' && (
          <Description
            dangerouslySetInnerHTML={{
              __html: `${shortDescription}`,
            }}
          />
        )}
        {plu ? <InfoRow title={__('Kód produktu')} value={plu} /> : null}
        {ean ? <InfoRow title={__('EAN')} value={ean} /> : null}
        {internalCode ? (
          <InfoRow title={__('REF kód')} value={internalCode} />
        ) : null}
        {warranty ? <InfoRow title={__('Záruka')} value={warranty} /> : null}
        {manufacturer ? (
          <InfoRow title={__('Výrobca')} value={manufacturer} />
        ) : null}
        {pckg ? (
          <InfoRow title={__('Balenie')} value={pckg} hasBorderBottom={true} />
        ) : null}
        <StockInfoRow
          title={__('Dostupnosť')}
          onStock={onStock}
          availability={prop(
            selectedGood,
            'availability.translations.0.avail_name',
          )}
          orderAvailable={prop(selectedGood, 'availability.order_available')}
          hasBorderBottom={false}
        />
        <MobilePartWrapper>{bottomPartMobile()}</MobilePartWrapper>
        <DesktopPartWrapper>{bottomPartDesktop()}</DesktopPartWrapper>
        {/* {shareButton} */}
      </>
    );
  };
  return (
    <>
      <DesktopWrapper>{rightUpperContent()}</DesktopWrapper>
      <MobileWrapper>{rightUpperContent()}</MobileWrapper>
    </>
  );
}

const DesktopPartWrapper = styled.div`
  display: block;
  margin-top: ${rem(36)};

  ${({ theme }) => theme.mediab.s450`
    display: none;
  `}
`;

const MobilePartWrapper = styled.div`
  display: none;
  margin-top: ${rem(36)};
  position: relative;
  ${({ theme }) => theme.mediab.s450`
    display: block;
  `}
`;
const Wrapper = styled(FlexCol)`
  width: 50%;
  margin-left: ${rem(32)};

  ${({ theme }) => theme.mediab.l1050`
     margin: 0 ${rem(24)};
  `}

  ${({ theme }) => theme.mediab.m810`
    margin: 0;
    width: 100%;
  `}

  ${({ theme }) => theme.mediab.s450`
    margin: 0;
  `}
`;

const DesktopWrapper = styled(Wrapper)`
  ${({ theme }) => theme.mediab.l925`
    display: none;
  `}

  background-color: ${({ theme }) => theme.colors.white};
  border: ${rem(1)} solid #C2CED9;
  box-shadow: 0px ${rem(2)} ${rem(8)} #4D617C14;
  border-radius: ${rem(8)};
  padding: ${rem(24)};

`;

const MobileWrapper = styled(Wrapper)`
  display: none;

  background-color: ${({ theme }) => theme.colors.white};
  border: ${rem(1)} solid #c2ced9;
  box-shadow: 0px ${rem(2)} ${rem(8)} #4d617c14;
  border-radius: ${rem(8)};
  padding: ${rem(24)};

  ${({ theme }) => theme.mediab.l925`
   display: flex;
   max-width: ${rem(600)};
   margin: 0 auto ${rem(26)} auto;
  `}
`;

const Description = styled.div`
  margin-bottom: ${rem(24)};
  font-size: ${rem(14)};
  font-family: ${({ theme }) => theme.font.primary};
  line-height: ${rem(22)};
`;

const PartWrapper = styled(FlexRowCenterVertical)`
  margin-top: ${rem(16)};
  margin-bottom: ${rem(8)};
  justify-content: start;
  width: 100%;
  position: relative;
  ${({ theme }) => theme.mediab.s450`
  flex-wrap: wrap;
  `}
`;

const CartButton = styled(ButtonPrimary)`
  width: 100%;

  &:disabled {
    background-color: #dcd6d6;
    color: ${({ theme }) => theme.colors.black};

    svg {
      fill: ${({ theme }) => theme.colors.black};

      * {
        fill: inherit;
      }
    }
  }

  ${({ theme }) => theme.mediab.s450`
    margin: ${rem(24)} auto;
  `}
`;

const ProductTitle = styled(Title)`
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.tertiary};
  font-weight: 700;

  ${({ theme }) => theme.mediab.m810`
    display: none;
  `};
`;

const AttribRessetWrapper = styled.div`
  font-family: ${({ theme }) => theme.font.primaryFont};
  padding: ${rem(12)} 0 ${rem(0)} 0;
  ${({ theme }) => theme.mediab.s450`
    display: flex;
    justify-content: center;
  `}
`;

const AttribRessetButton = styled(ButtonPrimary)`
  font-family: ${({ theme }) => theme.font.primaryFont};
  height: ${rem(32)};
  font-size: ${rem(12)};
  ${({ theme }) => theme.mediab.s450`
    margin-bottom: ${rem(12)};
    width: 70%;
  `}
`;
const AttribWrapper = styled(FlexCol)`
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
`;
const AttribSelectWrapper = styled.div`
  width: 49%;
  ${({ theme }) => theme.mediab.s450`
    width: 100%
  `}
`;

const CountWrapper = styled(FlexCol)`
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  ${({ theme }) => theme.mediab.s450`
  margin-top: ${rem(14)}
`}
`;

export { RightUpper as SanitasRightUpper };
